/**
 * AuthCallback.jsx - OAuth Callback Handler
 * ==========================================
 * Gestisce i redirect da OAuth providers (Google, LinkedIn, Apple, Facebook)
 * 
 * FLUSSO:
 * 1. Utente clicca "Login con Google"
 * 2. Supabase redirige a Google
 * 3. Google autentica e redirige a /auth/callback
 * 4. Questa pagina:
 *    - Recupera la session
 *    - Attende che il trigger crei il profilo
 *    - Verifica se onboarding completato
 *    - Redirige a /onboarding o /dashboard
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import supabase from '../lib/supabase';
import { toast } from 'sonner';
import { isProfileComplete } from '../utils/authUtils';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Debug logger con prefisso
const callbackLog = (action, data = {}) => {
  const timestamp = new Date().toISOString().substr(11, 8);
  console.log(`[OAUTH_CALLBACK] [${timestamp}] ${action}`, data);
};

/**
 * Fetch profilo con retry progressivo
 * Delays: 0ms → 300ms → 800ms (totale ~3 tentativi in ~1.1s)
 */
const fetchProfileWithRetry = async (userId, maxRetries = 3) => {
  const delays = [0, 300, 800];
  
  callbackLog('fetchProfile: Starting', { userId, maxRetries });
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // Apply delay (skip for first attempt)
    if (attempt > 0) {
      const delay = delays[Math.min(attempt, delays.length - 1)];
      callbackLog(`fetchProfile: Retry ${attempt + 1}/${maxRetries}, waiting ${delay}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        // PGRST116 = no rows returned (profile not created yet by trigger)
        if (error.code === 'PGRST116') {
          callbackLog(`fetchProfile: Profile not found (attempt ${attempt + 1}/${maxRetries})`);
          continue; // Retry
        }
        
        // Other DB error
        callbackLog('fetchProfile: DB Error', { code: error.code, message: error.message });
        return { data: null, error };
      }
      
      // Success!
      callbackLog('fetchProfile: SUCCESS', { 
        email: data?.email,
        role_professional: data?.role_professional,
        onboarding_completed: data?.onboarding_completed
      });
      
      return { data, error: null };
      
    } catch (err) {
      callbackLog(`fetchProfile: Exception (attempt ${attempt + 1})`, { error: err.message });
    }
  }
  
  // All retries exhausted
  callbackLog('fetchProfile: All retries failed', { userId, attempts: maxRetries });
  return { data: null, error: { message: 'Profilo non creato. Riprova.' } };
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const hasProcessed = useRef(false);
  
  // UI States
  const [status, setStatus] = useState('processing'); // processing | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        callbackLog('Processing OAuth callback...');
        callbackLog('URL hash', { hash: window.location.hash?.substring(0, 50) + '...' });
        callbackLog('URL search', { search: window.location.search?.substring(0, 50) + '...' });
        
        // Get current session from Supabase
        // Supabase auto-processes the hash/params from OAuth redirect
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        callbackLog('Session check', { 
          hasSession: !!session,
          hasUser: !!session?.user,
          error: sessionError?.message
        });
        
        if (sessionError) {
          callbackLog('Session error', { error: sessionError.message });
          throw new Error(sessionError.message);
        }
        
        if (!session?.user) {
          callbackLog('No session found - redirecting to login');
          toast.error('Autenticazione fallita');
          navigate('/login', { replace: true });
          return;
        }
        
        // Log user info
        callbackLog('OAuth session established', { 
          email: session.user.email,
          userId: session.user.id,
          provider: session.user.app_metadata?.provider
        });
        
        // Wait a moment for Supabase trigger to create profile
        callbackLog('Waiting for profile trigger...');
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Fetch profile with retry
        const { data: profile, error: profileError } = await fetchProfileWithRetry(session.user.id);
        
        if (profileError) {
          callbackLog('Profile error after retries', { error: profileError.message });
          setStatus('error');
          setErrorMessage('Errore nella creazione del profilo. Il trigger potrebbe non essere attivo.');
          return;
        }
        
        if (!profile) {
          callbackLog('Profile is null after retries');
          setStatus('error');
          setErrorMessage('Profilo non trovato. Contatta il supporto.');
          return;
        }
        
        callbackLog('Profile loaded successfully', {
          id: profile.id,
          email: profile.email,
          role_professional: profile.role_professional,
          onboarding_completed: profile.onboarding_completed,
          city: profile.city
        });
        
        // Refresh profile in auth context
        await refreshProfile();
        
        // Clear URL hash and params (remove OAuth tokens from URL)
        window.history.replaceState(null, '', '/auth/callback');
        
        // Check if profile is complete
        const profileComplete = isProfileComplete(profile);
        
        callbackLog('Profile completeness check', { 
          isComplete: profileComplete,
          role_professional: profile.role_professional,
          onboarding_completed: profile.onboarding_completed
        });
        
        if (profileComplete) {
          callbackLog('Profile complete → /dashboard');
          toast.success(`Bentornato, ${profile.first_name || profile.email}!`);
          navigate('/dashboard', { replace: true });
        } else {
          callbackLog('Profile incomplete → /onboarding');
          toast.info('Completa il tuo profilo per continuare');
          navigate('/onboarding', { replace: true });
        }
        
      } catch (error) {
        callbackLog('FATAL ERROR', { error: error.message });
        setStatus('error');
        setErrorMessage(error.message || 'Errore di autenticazione');
      }
    };

    processAuth();
  }, [navigate, refreshProfile]);

  // Error state UI
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-light text-white mb-3">Errore di Autenticazione</h2>
          <p className="text-white/50 text-sm mb-6">{errorMessage}</p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate('/login', { replace: true })}
              className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
            >
              Torna al Login
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                hasProcessed.current = false;
                setStatus('processing');
                window.location.reload();
              }}
              className="text-white/60 hover:text-white"
            >
              Riprova
            </Button>
          </div>
          
          {/* Debug info for developers */}
          <div className="mt-8 p-4 bg-white/5 rounded-lg text-left">
            <p className="text-white/40 text-xs font-mono mb-2">Debug Info:</p>
            <p className="text-white/30 text-xs font-mono break-all">{errorMessage}</p>
            <p className="text-white/30 text-xs font-mono mt-2">
              Apri la console (F12) per vedere i log [OAUTH_CALLBACK]
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Processing state UI
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#b08b5c] mx-auto mb-6" />
        <p className="text-white/60">Completamento accesso...</p>
        <p className="text-white/30 text-xs mt-2">Verifica sessione in corso</p>
      </div>
    </div>
  );
};

export default AuthCallback;
